import { writable } from 'svelte/store';

// Create a store for the current step
export const currentStep = writable(0);

// Create a store for survey responses
export const surveyStore = writable({});

export const recommendationStore = writable(null);

// Answer ID to value mappings based on API response data
// Update answerMappings for the new question options
export const answerMappings = {
  // Question 2: How many games do you want access to?
  attendance: {
    'a': '1-5',
    'b': '6-10',
    'c': '11-20',
    'd': '21+'
  },
  // Question 3: Where do you typically like to sit?
  seating: {
    'a': 'balcony',
    'b': 'premium'
  },
  // Question 7: How important is playoff ticket access to you?
  playoffImportance: {
    'a': 'want-membership-seats',
    'b': 'want-guaranteed-seats',
    'c': 'want-some-tickets',
    'd': 'dont-care'
  }
};

// Update answerTextMappings as well
export const answerTextMappings = {
  // Question 1
  inspiration: {
    'a': 'Create lifelong memories with family/friends',
    'b': 'Elevate my Cavs experience',
    'c': 'Join a passionate community of fans',
    'd': 'Support Cleveland\'s growth',
    'e': 'Entertaining Clients and Employees'
  },
  // Question 2 
  attendance: {
    'a': '1-2 games',
    'b': '6-10 games',
    'c': '11-20 games',
    'd': '21+ games'
  },
  // Question 3
  seating: {
    'a': 'Balcony Level Seating',
    'b': 'Premium Seating(Floor Seats)',
  },
  // Question 4
  companions: {
    'a': 'Family',
    'b': 'Friends',
    'c': 'Work Colleagues/Clients',
    'd': 'Significant Other',
    'e': 'I like to go by myself'
  },// Question 6
  motivations: {
    'a': 'Spending quality time with family',
    'b': 'Socializing with Friends',
    'c': 'Networking with Colleagues',
    'd': 'Hosting clients or business partners',
    'e': 'Watching future NBA players',
    'f': 'Promotional giveaways'
  },
  // Question 7
  playoffImportance: {
    'a': 'I want my membership seats for Playoff games',
    'b': 'I want guaranteed seats for the Playoffs, but don\'t care where they are located',
    'c': 'I don\'t need to go to every Playoff game, but want access to some tickets',
    'd': 'I don\'t really care about Playoff tickets'
  },
  // Question 9 (perks)
  perks: {
    'a': 'Team Shop Discount at Public Hall and Rocket Arena',
    'b': 'Exclusive events and experiences',
    'c': 'Priority access to playoff tickets',
    'd': 'Dedicated member specialist',
    'e': 'No fees on additional tickets',
    'f': 'Access to premium seats with food and drinks included',
    'g': 'Flexibility to exchange games you cannot attend'
  },
  promotions: {
    'a': 'Bobblehead Giveaways',
    'b': 'T-Shirt or other apparel giveaways',
    'c': 'Hoops n Hounds',
    'd': 'Post-game concerts',
    'e': 'Post-game autographs and free throws after every home game',
    'f': 'Community Impact Nights (PRIDE, Black Heritage Celebration, Special Olympics Dat, etc.)'
  },
};

// Function to initialize the survey with default values
export function initializeSurvey() {
  surveyStore.set({});
  currentStep.set(0);
}

// Function to reset survey to beginning
export function resetSurvey() {
  // Clear all survey data
  surveyStore.set({});
  // Return to the first step (or possibly the intro step)
  currentStep.set(0);
  
  // Optional: Scroll to top of the survey container
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

// Helper function to translate answer IDs to descriptive values
export function translateAnswerId(questionType, answerId) {
  // Check if mapping exists for this question type
  if (!answerMappings[questionType]) {
    console.log(`No mapping found for question type: ${questionType}`);
    return answerId;
  }
  
  // Check if answer ID exists in the mapping
  if (!answerMappings[questionType][answerId]) {
    console.log(`No mapping found for answer ID: ${answerId} in question type: ${questionType}`);
    return answerId;
  }
  
  return answerMappings[questionType][answerId];
}

// Helper function to determine recommendation based on answers
export function calculateRecommendation(answers) {
  console.log('Raw survey answers:', answers);
  
  if (!answers) {
    console.error('No survey answers provided to calculateRecommendation');
    return 'Half / Bowl'; // Default fallback
  }
  
  // Get answer IDs from various possible question keys, with defensive programming
  const attendanceId = answers.attendance || answers.q2 || answers['multi-choice-2'] || '';
  const seatingId = answers.seating || answers.q3 || answers['seating-3'] || '';
  const playoffImportanceId = answers.playoffImportance || answers.q7 || answers['multi-choice-7'] || '';
  const perks = answers.perks || answers.q9 || answers['multi-select-9'] || [];

  // Default values if translation fails
  let attendance = '1-5';  // Most conservative default
  let seating = 'balcony'; // Most conservative default
  let playoffImportance = 'want-some-tickets';
  
  try {
    attendance = translateAnswerId('attendance', attendanceId) || attendance;
    seating = translateAnswerId('seating', seatingId) || seating;
    playoffImportance = translateAnswerId('playoffImportance', playoffImportanceId) || playoffImportance;
  } catch (error) {
    console.error('Error translating answer IDs:', error);
  }

  // Check if perk 'f' (premium seats with food & drinks) was selected.
  // perks may be a single string ID or an array of IDs.
  const perksArray = Array.isArray(perks) ? perks : [perks];
  const wantsPremiumSeats = perksArray.includes('f');

  try {
    // If the user selected the premium seats + food & drinks perk, always
    // recommend Courtside — Half for no answer / 1-10 games, Full for 11+.
    if (wantsPremiumSeats) {
      const isFullAttendance = attendance === '11-20' || attendance === '21+';
      return isFullAttendance ? 'Full / Courtside' : 'Half / Courtside';
    }

    // Full Season Packages
    if (attendance === '11-20' || attendance === '21+') {
      return seating === 'premium' ? 'Full / Courtside' : 'Full / Bowl';
    }
    
    // Half Season Packages (1-5, 6-10, or no answer)
    return seating === 'premium' ? 'Half / Courtside' : 'Half / Bowl';

  } catch (error) {
    console.error('Error in recommendation logic:', error);
    return 'Half / Bowl';
  }
}

// Convert recommendation code to human-readable format
export function formatRecommendation(code) {
  console.log('Formatting recommendation code:', code);

  // Handle undefined or null input
  if (!code) {
    console.error('No code provided to formatRecommendation');
    return {
      packageName: "Half Season Membership",
      details: {
        games: '12 Regular Season Games',
        pricing: 'From $14.00 per game or $168 total per plan',
        oldMonthlyPrice: 16,
        monthlyPrice: 14,
        oldSeatPrice: 170,
        seatPrice: 168,
        savings: 'Significant savings over single game pricing with no fees',
        addedValue: 'Never waste a ticket in your plan! Ticket exchange provides ultimate flexibility',
        benefits: 'Exclusive Events and Experiences, Flexible ticket Exchange Program, Flexible Payment Plans'
      }
    };
  }

  // Parse the code parts
  let packageType, level;

  if (code.includes('/')) {
    [packageType, level] = code.split('/').map(s => s.trim());
  } else if (code.includes(' ')) {
    const parts = code.split(' ');
    packageType = parts[0].trim();
    level = parts.length > 1 ? parts.slice(1).join(' ').trim() : null;
  } else {
    packageType = code.trim();
    level = '';
  }

  // Ensure packageType is valid
  if (!['Half', 'Full'].includes(packageType)) {
    console.warn(`Unknown package type: ${packageType}, defaulting to Half`);
    packageType = 'Half';
    level = level || 'Bowl';
  }

  // Updated package details
  const packageDetails = {
    'Half': {
      games: '12 Regular Season Games',
      savings: 'Significant savings over single game pricing with no fees'
    },
    'Full': {
      games: '24 Regular Season Games',
      savings: 'Significant savings over single game pricing with no fees'
    },
  };

  const addedValues = {
    'Bowl': {
      addedValue: 'Never waste a ticket in your plan!  Ticket exchange provides ultimate flexibility'
    },
    'Courtside': {
      addedValue: 'Byrider Courtside Club Members receive an invitation to a premium event in addition to Meet the Team '
    }
  };
  // Numeric pricing values with old prices
  const pricingValues = {
    'Bowl': {
      'Half': { oldGamePrice: 191, gamePrice: 14, oldSeatPrice: 150, seatPrice: 168 },
      'Full': { oldGamePrice: 114, gamePrice: 12, oldSeatPrice: 1371, seatPrice: 288 }
    },
    'Courtside': {
      'Half': { oldGamePrice: 164, gamePrice: 70, oldSeatPrice: 1964, seatPrice: 840 },
      'Full': { oldGamePrice: 99, gamePrice: 65, oldSeatPrice: 1190, seatPrice: 1560 }
    }
  };
  
  // Create the package name
  let packageName;
  if (packageType === 'Half') {
    if (level === 'Bowl')
      packageName = 'Half Season Membership';
    if (level === 'Courtside')
      packageName = 'Half Byrider Courtside Club Membership';
  } 
  if (packageType === 'Full') {
    if (level === 'Bowl')
      packageName = 'Full Season Membership';
    if (level === 'Courtside')
      packageName = 'Full Byrider Courtside Club Membership';
  } 

  // Get numeric pricing for the selected level & package
  let oldGamePrice = null;
  let gamePrice = null;
  let oldSeatPrice = null;
  let seatPrice = null;

  if (level && pricingValues[level] && pricingValues[level][packageType]) {
    oldGamePrice = pricingValues[level][packageType].oldGamePrice;
    gamePrice = pricingValues[level][packageType].gamePrice;
    oldSeatPrice = pricingValues[level][packageType].oldSeatPrice;
    seatPrice = pricingValues[level][packageType].seatPrice;
  } 

  // Construct dynamic pricing text
  let pricingText = '';
  if (oldGamePrice && gamePrice && oldSeatPrice && seatPrice) {
    pricingText = `From $${gamePrice}/per game Or from $${seatPrice} total per plan`;
  } else {
    pricingText = 'Contact for Pricing';
  }

  // Update store
  recommendationStore.set({
    packageName,
    details: {
      games: packageDetails[packageType].games,
      pricing: pricingText,
      level:level,
      type: packageType,
      oldGamePrice,
      gamePrice,
      oldSeatPrice,
      seatPrice,
      savings: packageDetails[packageType].savings,
      addedValue: addedValues[level].addedValue,
      benefits: 'Ticket Exchange & two selected benefits'
    }
  });

  return {
    packageName,
    details: {
      games: packageDetails[packageType].games,
      level:level,
      type: packageType,
      pricing: pricingText,
      oldGamePrice,
      gamePrice,
      oldSeatPrice,
      seatPrice,
      addedValue: addedValues[level].addedValue,
      savings: packageDetails[packageType].savings,
      playoffAccess: packageDetails[packageType].playoffAccess,
      benefits: 'Ticket Exchange & two selected benefits'
    }
  };
}